import type { RefereeApiClient, RefereeToken } from './api-client';
import type { RealtimeTranscript } from './api-types';

type RealtimeClient = Pick<RefereeApiClient, 'createRealtimeSession'>;

export type RealtimeConnectionState = 'connecting' | 'listening' | 'unavailable';

export interface RealtimeTranscriptionCallbacks {
  onTranscript?: (transcript: RealtimeTranscript) => void;
  onStateChange?: (state: RealtimeConnectionState) => void;
}

export interface RealtimeTranscriptionHandle {
  activate(): void;
  pause(): void;
  resume(): void;
  finish(): Promise<RealtimeTranscript | undefined>;
  dispose(): void;
}

interface RealtimeEvent {
  type?: string;
  item_id?: string;
  previous_item_id?: string | null;
  transcript?: string;
  item?: { id?: string };
}

/**
 * Reconciles final transcription events by committed item id. OpenAI explicitly does not promise
 * completion events across turns arrive in order, so arrival order is only the last-resort fallback.
 */
export class RealtimeTranscriptAssembler {
  private readonly committedOrder: string[] = [];
  private readonly completedArrival: string[] = [];
  private readonly transcripts = new Map<string, string>();

  committed(itemId: string): void {
    if (itemId && !this.committedOrder.includes(itemId)) this.committedOrder.push(itemId);
  }

  completed(itemId: string, transcript: string): string {
    if (!itemId) return this.text();
    if (!this.completedArrival.includes(itemId)) this.completedArrival.push(itemId);
    const clean = transcript.trim();
    if (clean) this.transcripts.set(itemId, clean);
    return this.text();
  }

  text(): string {
    const ordered = [
      ...this.committedOrder,
      ...this.completedArrival.filter((id) => !this.committedOrder.includes(id)),
    ];
    return ordered
      .map((id) => this.transcripts.get(id))
      .filter((value): value is string => !!value)
      .join('\n\n')
      .trim();
  }
}

/**
 * Prepare a direct browser WebRTC audio path using a CLONE of the recorder's microphone track.
 * The clone starts disabled, so mic-check speech is never transcribed. Any setup failure returns
 * null and leaves MediaRecorder untouched.
 */
export async function createRealtimeTranscription(
  client: RealtimeClient,
  token: RefereeToken,
  stream: MediaStream,
  callbacks: RealtimeTranscriptionCallbacks = {},
): Promise<RealtimeTranscriptionHandle | null> {
  if (typeof RTCPeerConnection === 'undefined' || typeof MediaStream === 'undefined') return null;
  const sourceTrack = stream.getAudioTracks()[0];
  if (!sourceTrack || typeof sourceTrack.clone !== 'function') return null;

  callbacks.onStateChange?.('connecting');
  const peer = new RTCPeerConnection();
  const track = sourceTrack.clone();
  track.enabled = false;
  const clonedStream = new MediaStream([track]);
  peer.addTrack(track, clonedStream);

  const channel = peer.createDataChannel('oai-events');
  const assembler = new RealtimeTranscriptAssembler();
  let sessionId = '';
  let sessionProof = '';
  let disposed = false;
  let speechActive = false;
  let healthy = true;
  let lastCompletionAt = 0;
  let lastSpeechStoppedAt = 0;
  const pendingItems = new Set<string>();
  let latest: RealtimeTranscript | undefined;

  const markUnavailable = () => {
    healthy = false;
    if (!disposed) callbacks.onStateChange?.('unavailable');
  };

  channel.addEventListener('open', () => {
    if (!disposed && track.enabled) callbacks.onStateChange?.('listening');
  });
  channel.addEventListener('error', markUnavailable);
  channel.addEventListener('close', markUnavailable);
  peer.addEventListener('connectionstatechange', () => {
    if (peer.connectionState === 'failed' || peer.connectionState === 'disconnected') markUnavailable();
  });

  channel.addEventListener('message', (message: MessageEvent<string>) => {
    let event: RealtimeEvent;
    try {
      event = JSON.parse(message.data) as RealtimeEvent;
    } catch {
      return;
    }

    if (event.type === 'input_audio_buffer.speech_started') {
      speechActive = true;
      return;
    }
    if (event.type === 'input_audio_buffer.speech_stopped') {
      speechActive = false;
      lastSpeechStoppedAt = Date.now();
      return;
    }
    if (event.type === 'input_audio_buffer.committed') {
      if (event.item_id) {
        assembler.committed(event.item_id);
        pendingItems.add(event.item_id);
      }
      return;
    }
    if (event.type === 'conversation.item.created' && event.item?.id) {
      assembler.committed(event.item.id);
      return;
    }
    if (event.type !== 'conversation.item.input_audio_transcription.completed'
        || !event.item_id
        || typeof event.transcript !== 'string') {
      return;
    }

    const content = assembler.completed(event.item_id, event.transcript);
    pendingItems.delete(event.item_id);
    lastCompletionAt = Date.now();
    if (!content || !sessionId || !sessionProof) return;
    latest = { sessionId, sessionProof, content };
    callbacks.onTranscript?.(latest);
  });

  const close = () => {
    if (disposed) return;
    disposed = true;
    track.enabled = false;
    track.stop();
    try {
      channel.close();
    } catch {
      // best effort
    }
    peer.close();
  };

  try {
    const offer = await peer.createOffer();
    await peer.setLocalDescription(offer);
    await waitForIceGathering(peer, 2_000);
    const offerSdp = peer.localDescription?.sdp;
    if (!offerSdp) {
      close();
      return null;
    }

    const session = await client.createRealtimeSession(token, offerSdp);
    if (!session) {
      close();
      return null;
    }
    sessionId = session.sessionId;
    sessionProof = session.sessionProof;
    await peer.setRemoteDescription({ type: 'answer', sdp: session.answerSdp });
    await waitForDataChannelOpen(channel, 4_000);
  } catch {
    close();
    return null;
  }

  return {
    activate() {
      if (disposed) return;
      track.enabled = true;
      callbacks.onStateChange?.('listening');
    },
    pause() {
      track.enabled = false;
    },
    resume() {
      if (disposed) return;
      track.enabled = true;
      callbacks.onStateChange?.('listening');
    },
    async finish() {
      track.enabled = false;
      // Let server VAD close the last turn and deliver its final transcript. This is bounded; if
      // the final event never arrives, returning undefined deliberately triggers batch fallback.
      const deadline = Date.now() + 3_000;
      const finishStartedAt = Date.now();
      let settled = false;
      while (Date.now() < deadline) {
        const quietAfterCompletion = latest
          && !speechActive
          && pendingItems.size === 0
          && lastCompletionAt >= lastSpeechStoppedAt
          && Date.now() - finishStartedAt >= 900
          && Date.now() - lastCompletionAt >= 250;
        if (quietAfterCompletion) {
          settled = true;
          break;
        }
        await delay(50);
      }
      const result = healthy && settled ? latest : undefined;
      close();
      return result;
    },
    dispose: close,
  };
}

function waitForDataChannelOpen(channel: RTCDataChannel, timeoutMs: number): Promise<void> {
  if (channel.readyState === 'open') return Promise.resolve();
  return new Promise((resolve, reject) => {
    const timeout = setTimeout(() => done(new Error('Realtime data channel did not open.')), timeoutMs);
    channel.addEventListener('open', onOpen);
    channel.addEventListener('error', onError);

    function onOpen() {
      done();
    }

    function onError() {
      done(new Error('Realtime data channel failed.'));
    }

    function done(error?: Error) {
      clearTimeout(timeout);
      channel.removeEventListener('open', onOpen);
      channel.removeEventListener('error', onError);
      if (error) reject(error);
      else resolve();
    }
  });
}

function waitForIceGathering(peer: RTCPeerConnection, timeoutMs: number): Promise<void> {
  if (peer.iceGatheringState === 'complete') return Promise.resolve();
  return new Promise((resolve) => {
    const timeout = setTimeout(done, timeoutMs);
    peer.addEventListener('icegatheringstatechange', onChange);

    function onChange() {
      if (peer.iceGatheringState === 'complete') done();
    }

    function done() {
      clearTimeout(timeout);
      peer.removeEventListener('icegatheringstatechange', onChange);
      resolve();
    }
  });
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}
